/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import {
  BrickLoader,
  UniversalWrap,
  CostTableData,
  getColorsPalette,
  getTableColumns,
  mapAccumulatedCostChartColors,
  StackBarChart,
  tooltipStyles,
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
  getEndDate,
} from "~/components";
import { useCurrency } from "~/hooks";
import { useEnvAccumulatedCostChart } from "../hooks/useEnvAccumulatedCostChart";

type Props = {
  id: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function EnvAccumulatedCostView({
  id,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();
  const {
    envAccumulatedCostChartData,
    envAccumulatedCostTableData,
    envAccumulatedCostTotal,
    loading,
    error,
  } = useEnvAccumulatedCostChart({
    envId: id,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const columns = useMemo(
    () =>
      getTableColumns({
        startDate,
        endDate,
        currency,
        data: envAccumulatedCostTableData,
        hasFooter,
        period,
        chartType: ChartType.ACCUMULATED_COST,
        colorsPalette: getColorsPalette(ChartType.ACCUMULATED_COST),
      }),
    [startDate, endDate, currency, hasFooter, envAccumulatedCostTableData]
  );

  const colors = mapAccumulatedCostChartColors([CostAnalysisFields.DAILY_COST]);

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <>
            <StackBarChart
              width={parent.width}
              height={280}
              data={envAccumulatedCostChartData}
              granularity={granularity}
              loading={loading}
              startDate={startDate}
              endDate={endDate}
              period={period}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              keys={[CostAnalysisFields.DAILY_COST]}
              colors={colors}
              chartType={ChartType.ACCUMULATED_COST}
              containerRef={containerRef}
            />
            <When condition={tooltipOpen}>
              <TooltipInPortal
                // set this to random so it correctly updates with parent bounds
                key={Date.now()}
                top={tooltipTop}
                left={tooltipLeft}
                style={tooltipStyles}
              >
                {tooltipData}
              </TooltipInPortal>
            </When>
          </>
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={envAccumulatedCostTableData}
        chartType={ChartType.ACCUMULATED_COST}
        total={envAccumulatedCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
      />
    </UniversalWrap>
  );
}
